import { ru } from 'date-fns/locale/ru'
import {
  forwardRef,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts'
import {
  type ApiFinancialChartPoint,
  type ApiFinancialStats,
  type ApiMe,
  fetchFinancialStats,
} from '@/fetcher'
import { formatRub, formatUsdt } from '@/helpers/format'
import { CopyableText } from './CopyableText'
import { SectionCard } from './SectionCard'

type StatisticsPageProps = {
  me: ApiMe
}

type DateRange = [Date | null, Date | null]

registerLocale('ru', ru)

type DatePickerButtonProps = {
  className?: string
  value?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
}

const DatePickerButton = forwardRef<HTMLButtonElement, DatePickerButtonProps>(
  ({ className, value, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className={`statistics-date-input ${className ?? ''}`}
      onClick={onClick}
    >
      {value || 'Выберите период'}
    </button>
  ),
)

DatePickerButton.displayName = 'DatePickerButton'

const getToday = () => {
  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), today.getDate())
}

const getDateBefore = (date: Date, days: number) => {
  const result = new Date(date)
  result.setDate(result.getDate() - days)
  return result
}

const getQuickDateRange = (preset: string): DateRange => {
  const today = getToday()

  switch (preset) {
    case 'today':
      return [today, today]
    case 'week': {
      const monday = getDateBefore(today, (today.getDay() + 6) % 7)
      return [monday, today]
    }
    case 'month':
      return [new Date(today.getFullYear(), today.getMonth(), 1), today]
    case '7days':
      return [getDateBefore(today, 6), today]
    case '30days':
      return [getDateBefore(today, 29), today]
    default:
      return [null, null]
  }
}

const formatDateParam = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatThousands = (value: string | number) => {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '0k'

  const divisor = Math.abs(amount) >= 1_000_000 ? 1_000_000 : 1000
  const suffix = divisor === 1_000_000 ? 'M' : 'k'
  return `${new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 1,
  }).format(amount / divisor)}${suffix}`
}

const formatRate = (value: string | null) =>
  value === null ? '—' : `${formatUsdt(value)} RUB`

const formatRateAxis = (value: string | number) =>
  new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 1,
  }).format(Number(value))

const chartDateFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: 'numeric',
  month: 'short',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

const chartAxisDateFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
})

const chartAxisTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  hour: '2-digit',
  minute: '2-digit',
})

const formatChartDate = (value: string, isIntraday: boolean) => {
  const parts = chartDateFormatter.formatToParts(new Date(value))
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value.replace('.', '') ?? ''

  return isIntraday
    ? `${getPart('hour')}:${getPart('minute')}-${Number(getPart('hour')) + 3}:${getPart('minute')}`
    : `${getPart('day')} ${getPart('month')} (${getPart('weekday')})`
}

const formatChartAxisDate = (value: number) =>
  chartAxisDateFormatter.format(new Date(value))

const formatChartAxisTime = (value: number) =>
  chartAxisTimeFormatter.format(new Date(value))

const isIntradayChart = (data: ApiFinancialChartPoint[]) => {
  const firstDate = data[0]?.date
  const secondDate = data[1]?.date
  if (!firstDate || !secondDate) return false

  return (
    new Date(secondDate).getTime() - new Date(firstDate).getTime() <=
    3 * 60 * 60_000
  )
}

const StatLine = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--app-border)] py-3 last:border-b-0">
      <span className="text-sm text-[var(--app-muted)]">{label}</span>
      <CopyableText
        value={value}
        className="text-sm font-semibold text-[var(--app-text)]"
      />
    </div>
  )
}

const PaymentChart = ({ data }: { data: ApiFinancialChartPoint[] }) => {
  const intraday = isIntradayChart(data)
  const chartData = data.map((point) => ({
    ...point,
    amountRub: Number(point.amountRub),
    label: intraday
      ? formatChartAxisTime(new Date(point.date).getTime())
      : formatChartAxisDate(new Date(point.date).getTime()),
  }))

  return (
    <div className="payment-chart h-56 min-w-0 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 8, right: 4, left: 0, bottom: 8 }}
        >
          <CartesianGrid
            stroke="var(--app-border)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--app-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: 'var(--app-border)' }}
            minTickGap={12}
          />
          <YAxis
            tick={{ fill: 'var(--app-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) =>
              Number(value) === 0 ? '' : formatThousands(String(value))
            }
            tickMargin={0}
            width={32}
          />
          <Tooltip
            cursor={{ fill: 'var(--app-accent-soft)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]?.payload) return null

              const point = payload[0].payload as {
                date?: string
                amountRub?: number
              }
              if (!point.date || point.amountRub === undefined) return null

              return (
                <div className="rounded-[14px] border border-[var(--app-border)] bg-[var(--app-card)] px-3 py-2 text-sm text-[var(--app-text)]">
                  <div className="font-semibold">
                    {formatChartDate(point.date, intraday)}
                  </div>
                  <div className="text-[var(--app-accent)]">
                    Оплачено: {formatRub(String(point.amountRub))}
                  </div>
                </div>
              )
            }}
          />
          <Bar
            dataKey="amountRub"
            fill="var(--app-accent)"
            radius={[6, 6, 0, 0]}
            maxBarSize={48}
            activeBar={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

const RateChart = ({ data }: { data: ApiFinancialChartPoint[] }) => {
  const intraday = isIntradayChart(data)
  const points = data.flatMap((point) => {
    if (point.avgRate === null) return []
    const avgRate = Number(point.avgRate)
    const timestamp = new Date(point.date).getTime()
    const amountRub = Number(point.amountRub)
    if (![avgRate, timestamp, amountRub].every(Number.isFinite)) return []
    return [{ ...point, avgRate, timestamp, amountRub }]
  })
  const rates = points.map((point) => point.avgRate)
  const minRate = Math.min(...rates)
  const maxRate = Math.max(...rates)
  const padding = Math.max((maxRate - minRate) * 0.15, 0.5)
  const domain = rates.length
    ? [minRate - padding, maxRate + padding]
    : ['auto', 'auto']

  return (
    <div className="payment-chart h-56 min-w-0 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid
            stroke="var(--app-border)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={
              points.length
                ? [
                    points[0]?.timestamp ?? 'auto',
                    points.at(-1)?.timestamp ?? 'auto',
                  ]
                : ['auto', 'auto']
            }
            tick={{ fill: 'var(--app-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: 'var(--app-border)' }}
            minTickGap={12}
            tickFormatter={(value) =>
              intraday
                ? formatChartAxisTime(Number(value))
                : formatChartAxisDate(Number(value))
            }
          />
          <YAxis
            dataKey="avgRate"
            type="number"
            domain={domain}
            tick={{ fill: 'var(--app-muted)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => formatRateAxis(String(value))}
            tickMargin={0}
            width={48}
          />
          <ZAxis dataKey="amountRub" range={[40, 420]} />
          <Tooltip
            cursor={{ stroke: 'var(--app-accent)', strokeDasharray: '3 3' }}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]?.payload) return null

              const point = payload[0].payload as {
                date?: string
                avgRate?: string | number
              }
              if (!point.date || point.avgRate === undefined) return null

              return (
                <div className="rounded-[14px] border border-[var(--app-border)] bg-[var(--app-card)] px-3 py-2 text-sm text-[var(--app-text)]">
                  <div className="font-semibold">
                    {formatChartDate(point.date, intraday)}
                  </div>
                  <div className="text-[var(--app-accent)]">
                    Средний курс: {formatUsdt(String(point.avgRate))}
                  </div>
                </div>
              )
            }}
          />
          <Scatter
            data={points}
            fill="var(--app-accent)"
            line={{ stroke: 'var(--app-accent)', strokeWidth: 2 }}
            lineType="joint"
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  )
}

const StatisticsContent = ({
  stats,
  chart,
}: {
  stats: ApiFinancialStats
  chart: ApiFinancialChartPoint[]
}) => (
  <div className="space-y-4">
    <SectionCard title="📋 Заявки">
      <StatLine label="Создано" value={String(stats.createdRequestsCount)} />
      <StatLine label="" value={`${formatRub(stats.createdRequestsRub)} RUB`} />
      <StatLine label="Оплачено" value={String(stats.clientPaidCount)} />
      <StatLine label="" value={`${formatRub(stats.clientPaidRub)} RUB`} />
      <StatLine label="" value={`${formatUsdt(stats.clientPaidUsdt)} USDT`} />
      <PaymentChart data={chart} />
    </SectionCard>

    <SectionCard title="💱 Курс">
      <StatLine label="Средний" value={formatRate(stats.avgRate)} />
      <StatLine label="Минимальный" value={formatRate(stats.minRate)} />
      <StatLine label="Максимальный" value={formatRate(stats.maxRate)} />
      <RateChart data={chart} />
    </SectionCard>

    <SectionCard title="🏦 Баланс">
      <StatLine label="В пути" value={`${formatRub(stats.rubInTransit)} RUB`} />
      <StatLine
        label="Поступило"
        value={`${formatRub(stats.rubReceived)} RUB`}
      />
      <StatLine
        label="Оплачено и поступило"
        value={`${formatRub(stats.rubPaidAndReceived)} RUB`}
      />
    </SectionCard>

    <SectionCard title="📨 Переводы">
      <StatLine
        label="К отправке"
        value={`${formatUsdt(stats.usdtToSend)} USDT`}
      />
      <StatLine
        label="Отправлено"
        value={`${formatUsdt(stats.usdtSent)} USDT`}
      />
    </SectionCard>
  </div>
)

export const StatisticsPage = ({ me }: StatisticsPageProps) => {
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = getToday()
    return [today, today]
  })
  const [stats, setStats] = useState<ApiFinancialStats | null>(null)
  const [chart, setChart] = useState<ApiFinancialChartPoint[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [startDate, endDate] = dateRange

  useEffect(() => {
    if (me.teams.length === 1 && me.teams[0]?.id) {
      setSelectedTeamId(me.teams[0].id)
    }
  }, [me.teams])

  const loadStats = useCallback(async () => {
    if (
      !startDate ||
      !endDate ||
      (!selectedTeamId && me.user.role !== 'admin')
    ) {
      setStats(null)
      setChart([])
      return
    }

    setIsLoading(true)
    setErrorMessage(null)
    try {
      const result = await fetchFinancialStats({
        teamId: selectedTeamId || undefined,
        from: formatDateParam(startDate),
        to: formatDateParam(endDate),
      })
      setStats(result.stats)
      setChart(result.chart)
    } catch (error) {
      setStats(null)
      setChart([])
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Не удалось загрузить статистику',
      )
    } finally {
      setIsLoading(false)
    }
  }, [endDate, me.user.role, selectedTeamId, startDate])

  useEffect(() => {
    void loadStats()
  }, [loadStats])

  const handleDateChange = (dates: DateRange) => {
    setDateRange(dates)
  }

  return (
    <div className="py-8 mx-auto max-w-2xl space-y-4 px-4 pb-12">
      <div className="grid grid-cols-2 gap-2">
        <div className="relative min-w-0">
          <select
            id="stats-team"
            value={selectedTeamId}
            onChange={(event) => setSelectedTeamId(event.currentTarget.value)}
            className="block w-full min-w-0 appearance-none truncate rounded-[22px] bg-[var(--app-input)] px-4 py-3 pr-12 text-sm leading-6 text-[var(--app-text)] outline-none transition focus:border-[var(--app-accent)] focus:ring-4 focus:ring-[var(--app-accent-soft)]"
          >
            {me.user.role === 'admin' && <option value="">Все команды</option>}
            {me.teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[var(--app-muted)]">
            ↓
          </span>
        </div>
        <DatePicker
          selectsRange
          withPortal
          calendarClassName="telegram-datepicker-calendar"
          renderCustomHeader={({
            monthDate,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <>
              <div className="telegram-datepicker-quick-actions">
                {[
                  ['today', 'Сегодня'],
                  ['week', 'Неделя'],
                  ['month', 'Месяц'],
                  ['7days', '7д.'],
                  ['30days', '30д.'],
                ].map(([preset, label]) => (
                  <button
                    key={preset}
                    type="button"
                    className="telegram-datepicker-quick-action"
                    onClick={() =>
                      setDateRange(getQuickDateRange(preset ?? 'today'))
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="telegram-datepicker-month-header">
                <button
                  type="button"
                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                  aria-label="Предыдущий месяц"
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous" />
                </button>
                <span className="react-datepicker__current-month">
                  {new Intl.DateTimeFormat('ru-RU', {
                    month: 'long',
                    year: 'numeric',
                  }).format(monthDate)}
                </span>
                <button
                  type="button"
                  className="react-datepicker__navigation react-datepicker__navigation--next"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                  aria-label="Следующий месяц"
                >
                  <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next" />
                </button>
              </div>
            </>
          )}
          locale="ru"
          dateFormat="dd.MM"
          startDate={startDate}
          endDate={endDate}
          selected={startDate}
          onChange={handleDateChange}
          isClearable={false}
          id="stats-period"
          customInput={<DatePickerButton />}
        />
      </div>

      {errorMessage && (
        <div className="rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
          {errorMessage}
        </div>
      )}

      {isLoading && (
        <div className="rounded-[18px] bg-[var(--app-input)] px-4 py-6 text-center text-sm text-[var(--app-muted)]">
          Загрузка статистики...
        </div>
      )}

      {!isLoading && stats && <StatisticsContent stats={stats} chart={chart} />}
    </div>
  )
}
